import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CancelModalButton from 'components/Custom/CancelModalButton'
import CustomModalFooter from 'components/Custom/CustomModalFooter'
import CustomModalHeader from 'components/Custom/CustomModalHeader'
import SubmitModalButton from 'components/Custom/SubmitModalButton'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Input,
  Modal,
  ModalBody,
} from 'reactstrap'
import {
  createForm,
  fetchForm,
  resetFormFields,
  setNewFormFields,
  updateForm,
} from 'store/Forms/actions'
import { v4 as uuidv4 } from 'uuid'
import DropArea from './DropArea'
import { fieldIcon, formFields } from './FormFieldsData'
import RightSidebar from './RightSidebar'
import { renderCreateEditBody } from './formFunctions'
import ChangeLabelModal from './ChangeLabelModal'
import './CreateEditForm.css'

function CreateEditForm({
  showAddFormModal,
  setShowAddFormModal,
  editMode = false,
  formId = null,
}) {
  const formWrapperRef = useRef(null)
  const [isOutsideWrapper, setIsOutsideWrapper] = useState(false)

  // Safari detection for compatibility fixes
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

  const { newFormFields } = useSelector((store) => store.form)
  const dispatch = useDispatch()
  const [activeCard, setActiveCard] = useState(null)
  const [dragSource, setDragSource] = useState(null)

  const [currentTab, setCurrentTab] = useState(1)

  const [showRightSidebar, setShowRightSidebar] = useState(false)
  const [field, setField] = useState(null)

  // Change Label Modal state
  const [showChangeLabelModal, setShowChangeLabelModal] = useState(false)
  const [currentFieldForLabel, setCurrentFieldForLabel] = useState(null)

  const formTitle =
    newFormFields?.find((field) => field?.type === 'heading')?.text ||
    'Untitled Form'

  const handleDelete = (taskIndex) => {
    const newTasks = newFormFields.filter((task, index) => index !== taskIndex)
    dispatch(setNewFormFields(newTasks))
  }

  const updateFieldState = (fieldIndex, updates) => {
    const updatedFields = newFormFields.map((field) =>
      field.index === fieldIndex ? { ...field, ...updates } : field
    )
    dispatch(setNewFormFields(updatedFields))
  }

  const handleOpenLabelModal = (field) => {
    setCurrentFieldForLabel(field)
    setShowChangeLabelModal(true)
  }

  const handleLabelChange = (newLabel) => {
    if (currentFieldForLabel) {
      updateFieldState(currentFieldForLabel.index, { label: newLabel })
    }
  }

  const onDrop = (position) => {
    if (activeCard === null || activeCard === undefined) return

    if (dragSource === 'sidebar') {
      const newField = formFields[activeCard]

      newFormFields.splice(position, 0, {
        ...newField,
        index: uuidv4(),
      })

      dispatch(setNewFormFields(newFormFields))
    } else if (dragSource === 'dropzone') {
      const currentIndex = newFormFields.findIndex(
        (field) => field.index === activeCard
      )

      if (currentIndex === -1 || currentIndex === position) return

      const [draggedItem] = newFormFields.splice(currentIndex, 1)

      newFormFields.splice(position, 0, draggedItem)

      dispatch(setNewFormFields(newFormFields))
    }
  }

  useEffect(() => {
    if (editMode) {
      dispatch(fetchForm(formId))
    }
  }, [dispatch, editMode, formId])

  useEffect(() => {
    if (!editMode) {
      dispatch(resetFormFields())
    }
  }, [editMode, dispatch])

  const DropZone = () => {
    const [isDraggingOutside, setIsDraggingOutside] = useState(false)

    const handleDragLeave = () => {
      setIsDraggingOutside(true)
    }

    const handleDropInside = (position) => {
      setIsDraggingOutside(false)
      onDrop(position)
    }

    // const handleDragEnd = () => {
    //   if (isDraggingOutside && dragSource === 'dropzone') {
    //     // eslint-disable-next-line
    //     if (!confirm('Are you sure you want to remove this field')) {
    //       return
    //     }

    //     const updatedFields = newFormFields.filter(
    //       (field) => field.index !== activeCard
    //     )
    //     dispatch(setNewFormFields(updatedFields))
    //   }
    //   setIsDraggingOutside(false)
    //   setActiveCard(null)
    //   setDragSource(null)
    // }

    const handleDragEnd = (event) => {
      // Safari compatibility: Use setTimeout to ensure proper timing
      setTimeout(() => {
        const wrapper = formWrapperRef.current

        if (wrapper && dragSource === 'dropzone') {
          const { clientX, clientY } = event

          // Safari fallback: Check if coordinates are valid
          if (clientX !== 0 || clientY !== 0) {
            const { left, right, top, bottom } = wrapper.getBoundingClientRect()

            const isOutsideWrapper =
              clientX < left ||
              clientX > right ||
              clientY < top ||
              clientY > bottom

            if (isDraggingOutside && isOutsideWrapper) {
              // eslint-disable-next-line
              if (!confirm('Are you sure you want to remove this field')) {
                return
              }

              const updatedFields = newFormFields.filter(
                (field) => field.index !== activeCard
              )
              dispatch(setNewFormFields(updatedFields))
            }
          }
        }

        setIsDraggingOutside(false)
        setActiveCard(null)
        setDragSource(null)
      }, 0)
    }

    return (
      <div
        ref={formWrapperRef}
        className='p-4 border rounded card'
        style={{
          minHeight: '200px',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}
        onDragLeave={handleDragLeave}
        onDragEnd={handleDragEnd}
      >
        <DropArea onDrop={() => handleDropInside(0)} />
        {newFormFields.length === 0 ? (
          <p className='text-muted'>Drag fields here</p>
        ) : (
          newFormFields.map((field, index) => (
            <>
              <div
                draggable
                onDragStart={(e) => {
                  // Safari compatibility fixes
                  e.dataTransfer.effectAllowed = 'move'
                  e.dataTransfer.setData('text/html', '') // Required for Safari
                  setActiveCard(field.index)
                  setDragSource('dropzone')
                }}
                onDragEnd={(e) => {
                  // Safari compatibility: Prevent default and stop propagation
                  e.preventDefault()
                  e.stopPropagation()
                  handleDragEnd(e)
                }}
                style={{
                  cursor: 'pointer',
                }}
                onClick={() => {
                  // Don't open sidebar for attach file fields
                  if (field.type === 'upload' && field.inputType === 'attach') {
                    return
                  }
                  setShowRightSidebar(true)
                  setField(field)
                }}
              >
                {renderCreateEditBody(
                  field,
                  dispatch,
                  updateFieldState,
                  handleOpenLabelModal
                )}
              </div>
              <DropArea onDrop={() => handleDropInside(index + 1)} />
            </>
          ))
        )}
        <DropArea onDrop={() => handleDropInside(newFormFields.length)} />
      </div>
    )
  }

  return (
    <Modal
      toggle={() => setShowAddFormModal(false)}
      isOpen={showAddFormModal}
      centered
      size='xl'
      style={{
        maxWidth: '90%',
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
      }}
      className='scrollbar'
    >
      <CustomModalHeader
        title={!editMode ? 'Create Form' : 'Edit Form'}
        toggle={() => setShowAddFormModal(false)}
      />
      <ModalBody
        style={{
          flex: 1,
          overflowY: 'auto',
          paddingBottom: '0px',
        }}
      >
        {currentTab === 1 ? (
          <div
            className='d-flex align-items-start justify-content-between'
            style={{ gap: '2rem' }}
          >
            {/* Left Sidebar */}
            <div className='position-sticky' style={{ top: '3rem' }}>
              <Card className='p-3'>
                <div className='d-flex flex-column' style={{ gap: '0.8rem' }}>
                  <div className='d-grid grid-container-maintenance'>
                    {formFields.map((field, index) => (
                      <div
                        draggable
                        onDragStart={() => {
                          setActiveCard(index)
                          setDragSource('sidebar')
                        }}
                        onDragEnd={() => {
                          setActiveCard(null)
                          setDragSource(null)
                        }}
                        key={index}
                        className='p-2 border rounded border-dark'
                        style={{ cursor: 'move' }}
                        onClick={() =>
                          dispatch(
                            setNewFormFields([
                              ...newFormFields,
                              { ...field, index: uuidv4() },
                            ])
                          )
                        }
                      >
                        <FontAwesomeIcon
                          icon={fieldIcon[field.type]}
                          className='mr-2'
                        />

                        {field.text}
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>

            {/* Form Drop Zone */}
            <DropZone />

            {/* Right Sidebar */}
            {showRightSidebar ? (
              <RightSidebar
                setShowRightSidebar={setShowRightSidebar}
                setField={setField}
                field={field}
              />
            ) : (
              <div></div>
            )}
          </div>
        ) : (
          <Card className='w-25 mx-auto'>
            <CardHeader className='bg-info d-flex align-items-center justify-content-center'>
              <p className='text-capitalize text-white font-weight-bold'>
                Form Title
              </p>
            </CardHeader>
            <CardBody>
              <FormGroup className='ml-0 mb-0'>
                <label
                  role='button'
                  className='form-control-label'
                  htmlFor='formTitle'
                >
                  Title
                </label>
                <Input
                  defaultValue={formTitle}
                  id='formTitle'
                  type={'text'}
                  style={{
                    height: '38px',
                  }}
                  name='formTitle'
                />
              </FormGroup>
            </CardBody>
          </Card>
        )}
      </ModalBody>

      <CustomModalFooter
        style={{
          flexShrink: 0,
          borderTop: '1px solid #dee2e6',
          backgroundColor: 'white',
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
        }}
      >
        <SubmitModalButton
          customFn={() => {
            if (!editMode) {
              if (currentTab === 1) {
                setCurrentTab(currentTab + 1)
              } else {
                dispatch(createForm(formTitle))
                setShowAddFormModal(false)
              }
            } else {
              dispatch(updateForm(formId))
              setShowAddFormModal(false)
            }
          }}
          text={
            !editMode && currentTab === 1
              ? 'Next'
              : !editMode && currentTab === 2
              ? 'Create Form'
              : 'Update Form'
          }
        ></SubmitModalButton>
        <CancelModalButton
          customFn={() => {
            setShowAddFormModal(false)
          }}
        ></CancelModalButton>
      </CustomModalFooter>

      <ChangeLabelModal
        showModal={showChangeLabelModal}
        setShowModal={setShowChangeLabelModal}
        currentLabel={currentFieldForLabel?.label || ''}
        onLabelChange={handleLabelChange}
      />
    </Modal>
  )
}

export default CreateEditForm
